import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { EntityTypeOptionKey } from '@/types'
import { ArrowRight } from 'lucide-react'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (key: EntityTypeOptionKey) => void
}

type Step = 'investment' | 'owners' | 'result'

// At most two questions, then we answer for them — see the entity type
// step's spec. Investment intent decides the type; owner count only
// changes the reason we show, since an LLC handles either case fine.
export function HelpMeChooseDialog({ open, onOpenChange, onSelect }: Props) {
  const [step, setStep] = useState<Step>('investment')
  const [investing, setInvesting] = useState(false)
  const [multipleOwners, setMultipleOwners] = useState(false)

  const reset = () => {
    setStep('investment')
    setInvesting(false)
    setMultipleOwners(false)
  }

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next)
  }

  const answerInvestment = (yes: boolean) => {
    setInvesting(yes)
    setStep(yes ? 'result' : 'owners')
  }

  const answerOwners = (yes: boolean) => {
    setMultipleOwners(yes)
    setStep('result')
  }

  const resultKey: EntityTypeOptionKey = investing ? 'corporation' : 'llc'
  const resultReason = investing
    ? 'Most outside investors expect a C-Corp — this sets you up right for fundraising.'
    : multipleOwners
      ? 'An LLC lets you and your co-owners split things flexibly with an operating agreement.'
      : 'An LLC keeps a solo setup simple, with less paperwork than a C-Corp.'

  const useResult = () => {
    onSelect(resultKey)
    handleOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" onCloseAutoFocus={reset}>
        <DialogHeader>
          <DialogTitle>Help me choose</DialogTitle>
          <DialogDescription>A couple of quick questions and we'll pick for you.</DialogDescription>
        </DialogHeader>

        {step === 'investment' && (
          <div className="space-y-4">
            <p className="text-sm font-medium text-foreground">
              Are you planning to raise money from outside investors — VCs or angels?
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => answerInvestment(true)} className="flex-1">Yes</Button>
              <Button type="button" variant="outline" onClick={() => answerInvestment(false)} className="flex-1">Not right now</Button>
            </div>
          </div>
        )}

        {step === 'owners' && (
          <div className="space-y-4">
            <p className="text-sm font-medium text-foreground">Will there be more than one owner?</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => answerOwners(true)} className="flex-1">Yes</Button>
              <Button type="button" variant="outline" onClick={() => answerOwners(false)} className="flex-1">Just me</Button>
            </div>
          </div>
        )}

        {step === 'result' && (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/40 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">Our answer</p>
              <p className="text-base font-medium text-foreground mb-1">{resultKey === 'llc' ? 'LLC' : 'C-Corp'}</p>
              <p className="text-sm text-muted-foreground">{resultReason}</p>
            </div>
            <Button type="button" onClick={useResult} className="w-full">
              Use {resultKey === 'llc' ? 'LLC' : 'C-Corp'}
              <ArrowRight className="size-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
